import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthProviderFetchJson } from '../../lib/Auth';
import { superAdminApiServer } from '../../lib/config';
import { requestCacheMock } from './cacheMockData';

interface RequestCacheDetailItem {
  integrationUuid: string;
  externalId: string;
  employeeExternalId: string | null;
  payloadHash: string | null;
  sourceName: string | null;
  raw: Record<string, unknown> | null;
  updateReason: string | null;
  syncedAt: string | null;
  syncedErrorAt: string | null;
  syncedError: string | null;
  createdAt: string;
  updatedAt: string;
}

const DetailField = ({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minHeight: 24 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 220, flexShrink: 0, pt: '2px' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.8rem' : 'inherit' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const RequestCacheDetail = () => {
  const { integrationId, externalId } = useParams<{ integrationId: string; externalId: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const [item, setItem] = useState<RequestCacheDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const handleCopyJson = () => {
    if (!item) return;
    navigator.clipboard.writeText(JSON.stringify(item, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (!integrationId || !externalId) return;

    if (process.env.NODE_ENV === 'development') {
      const decoded = decodeURIComponent(externalId);
      const mock = requestCacheMock.items.find((i) => i.externalId === decoded);
      setItem(
        mock
          ? ({
              integrationUuid: integrationId,
              payloadHash: null,
              sourceName: 'requests',
              syncedErrorAt: null,
              syncedError: null,
              createdAt: mock.syncedAt ?? new Date().toISOString(),
              updatedAt: mock.syncedAt ?? new Date().toISOString(),
              ...mock
            } as RequestCacheDetailItem)
          : null
      );
      setLoading(false);
      return;
    }

    const url = `${superAdminApiServer}/integrations/${integrationId}/cache/requests/${encodeURIComponent(externalId)}`;
    getAuthProviderFetchJson(url)
      .then(({ json }) => setItem(json))
      .catch(() => notify('Failed to load request cache detail', { type: 'error' }))
      .finally(() => setLoading(false));
  }, [integrationId, externalId, notify]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography color="text.secondary">Request cache record not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h6">Request Cache: {item.externalId}</Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy JSON payload'}>
          <Button variant="outlined" size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={handleCopyJson}>
            {copied ? 'Copied!' : 'Copy JSON'}
          </Button>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 3 }}>
        <DetailField label="External ID" value={item.externalId} mono />
        <DetailField label="Employee External ID" value={item.employeeExternalId} mono />
        <DetailField label="Request Type" value={item.raw?.requestType as string} />
        <DetailField label="Subtype External ID" value={item.raw?.requestSubtypeExternalId as string} mono />
        <DetailField label="Start Time" value={item.raw?.startTime ? new Date(item.raw.startTime as string).toLocaleString() : null} />
        <DetailField label="End Time" value={item.raw?.endTime ? new Date(item.raw.endTime as string).toLocaleString() : null} />
        <DetailField label="Approval Status" value={item.raw?.approvalStatus as string} />
        <DetailField label="All Day" value={item.raw?.allDay != null ? String(item.raw.allDay) : null} />
        <DetailField label="Is Endless" value={item.raw?.isEndless != null ? String(item.raw.isEndless) : null} />
        <DetailField label="Timezone" value={item.raw?.timezone as string} />
        <DetailField label="Update Reason" value={item.updateReason} />
        <DetailField label="Synced At" value={item.syncedAt ? new Date(item.syncedAt).toLocaleString() : null} />
        <DetailField label="Synced Error At" value={item.syncedErrorAt ? new Date(item.syncedErrorAt).toLocaleString() : null} />
        <DetailField label="Synced Error" value={item.syncedError} />
        <DetailField label="Created At" value={item.createdAt ? new Date(item.createdAt).toLocaleString() : null} />
        <DetailField label="Updated At" value={item.updatedAt ? new Date(item.updatedAt).toLocaleString() : null} />
      </Box>

      {item.raw && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Raw Payload (JSON)
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 1,
              p: 2,
              fontSize: '0.78rem',
              fontFamily: 'monospace',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}
          >
            {JSON.stringify(item.raw, null, 2)}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RequestCacheDetail;
